import { bearing, distanceMi, interpolateGreatCircle, type LatLon } from './geo';

/**
 * Where a plane is at a given moment, for drawing it on a map, with or without a connection.
 *
 * - With a recent position report (wifi on board, or the server checked while there was
 *   signal), it starts there and keeps moving toward the destination at the reported ground
 *   speed, so the plane glides between reports instead of jumping.
 * - Without one, it's the best guess from timing: along the great circle by how much of the
 *   flying time has passed. If an older report exists (wifi dropped mid-flight), the guess
 *   starts from that last known spot and time rather than from the departure airport.
 */

export interface PlaneFix extends LatLon {
  /** Direction of travel, degrees clockwise from north. */
  heading: number;
  /** 'live' from a recent report; 'estimated' from timing. */
  source: 'live' | 'estimated';
  /** When the report it's based on was taken; null when it's from timing alone. */
  reportedAt: number | null;
}

export interface PositionReport extends LatLon {
  heading?: number | null;
  groundspeedKt?: number | null;
  /** When the plane was there (epoch ms). */
  at: number;
}

/** A report this recent is treated as live; older ones still anchor the estimate. */
export const LIVE_FOR_MS = 15 * 60_000;
const MPH_PER_KNOT = 1.15078;
/** Below this the reported speed is taxiing or nonsense; don't extrapolate with it. */
const MIN_FLYING_KT = 100;

function headingAt(p: LatLon, to: LatLon, fallback: number): number {
  return distanceMi(p, to) > 1 ? bearing(p, to) : fallback;
}

export function planeAt(opts: {
  from: LatLon;
  to: LatLon;
  /** Takeoff and landing, best guesses (null when unknown). */
  off: number | null;
  on: number | null;
  report?: PositionReport | null;
  now: number;
}): PlaneFix | null {
  const { from, to, off, on, report, now } = opts;
  const overall = bearing(from, to);

  // Live: carry the last report forward at its speed, toward the destination.
  if (report && now - report.at < LIVE_FOR_MS && now >= report.at - 60_000) {
    const kt = report.groundspeedKt ?? 0;
    let at: LatLon = { lat: report.lat, lon: report.lon };
    if (kt >= MIN_FLYING_KT) {
      const flown = (kt * MPH_PER_KNOT * Math.max(0, now - report.at)) / 3_600_000;
      const left = distanceMi(at, to);
      at = left > 0 ? interpolateGreatCircle(at, to, Math.min(1, flown / left)) : to;
    }
    return {
      ...at,
      heading:
        kt >= MIN_FLYING_KT
          ? headingAt(at, to, report.heading ?? overall)
          : (report.heading ?? headingAt(at, to, overall)),
      source: 'live',
      reportedAt: report.at,
    };
  }

  // Estimated: from the last known spot (or the departure airport) to the destination, by time.
  if (on == null) return null;
  const anchored = report != null && off != null && report.at > off && report.at < on;
  const start: LatLon = anchored ? { lat: report!.lat, lon: report!.lon } : from;
  const since = anchored ? report!.at : off;
  if (since == null) return null;
  const f = Math.min(1, Math.max(0, (now - since) / Math.max(60_000, on - since)));
  const at = interpolateGreatCircle(start, to, f);
  return {
    ...at,
    heading: headingAt(at, to, overall),
    source: 'estimated',
    reportedAt: anchored ? report!.at : null,
  };
}

/** Share of the trip flown, by distance (0 to 1), for drawing the flown part of the route. */
export function flownShare(from: LatLon, to: LatLon, at: LatLon): number {
  const total = distanceMi(from, to);
  if (total <= 0) return 1;
  return Math.min(1, Math.max(0, 1 - distanceMi(at, to) / total));
}

/** The flight bringing in the plane for mine, as of the last check. */
export interface InboundLeg {
  departed?: boolean;
  landed?: boolean;
  atGate?: boolean;
  /** Takeoff (or pushback) once it has gone, else when it's expected to. */
  departAt?: number | null;
  /** Gate arrival here, best guess. */
  estimatedIn?: number | null;
  position?: PositionReport | null;
}

/** Typical taxi in, to turn a gate arrival into a landing. */
const TAXI_IN = 8 * 60_000;

/**
 * Where the plane for my flight is: at its origin until it leaves, on its way here after that
 * (live or estimated, as above), and here once it has landed.
 */
export function inboundPlaneAt(
  leg: InboundLeg,
  origin: LatLon,
  here: LatLon,
  now: number,
): PlaneFix {
  const heading = bearing(origin, here);
  const at = (p: LatLon, source: PlaneFix['source']): PlaneFix => ({
    lat: p.lat,
    lon: p.lon,
    heading,
    source,
    reportedAt: null,
  });
  if (leg.landed || leg.atGate) return at(here, 'live');
  if (!leg.departed) return at(origin, 'estimated');
  const on = leg.estimatedIn != null ? leg.estimatedIn - TAXI_IN : null;
  return (
    planeAt({
      from: origin,
      to: here,
      off: leg.departAt ?? null,
      on,
      report: leg.position ?? null,
      now,
    }) ?? at(origin, 'estimated')
  );
}
